import React, { useEffect, useState } from "react";
import { Box, Typography, Button, TextField, InputAdornment } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";

import { fetchInfoCapitalGoalsData, fetchInfoCapitalIssuesData } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

export default function SmInfoCapitalGoalDataListPage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [goals, issues] = await Promise.all([
          fetchInfoCapitalGoalsData(businessDomainId).catch(() => []),
          fetchInfoCapitalIssuesData(businessDomainId).catch(() => []),
        ]);
        const combined = [
          ...(Array.isArray(goals) ? goals.map((g) => ({ ...g, _type: "goal" })) : []),
          ...(Array.isArray(issues) ? issues.map((i) => ({ ...i, _type: "issue" })) : []),
        ];
        setRows(combined);
      } catch (e) {
        notify.apiError(e);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId]);

  const filteredRows = searchText
    ? rows.filter((r) => r.successFactorName?.toLowerCase().includes(searchText.toLowerCase()))
    : rows;

  const columns = [
    {
      field: "_type",
      headerName: "種別",
      width: 110,
      renderCell: (params) => params.value === "issue" ? "課題（Issue）" : "目標（Goal）",
    },
    { field: "successFactorName", headerName: t("successFactor.name"), flex: 1, minWidth: 200 },
    { field: "successFactorDescription", headerName: t("successFactor.description"), flex: 1, minWidth: 200 },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("infoCapitalGoalData.list.title")}</Typography>
        <TextField size="small" placeholder={t("common.search")} value={searchText} onChange={(e) => setSearchText(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`/design/${businessDomainId}/strategy-map/info-capital-goals-data/create`)}
        >
          {t("common.add")}
        </Button>
      </Box>
      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(r) => r.successFactorId}
        loading={loading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        onRowClick={(p) => {
          const path = p.row._type === "issue"
            ? `/design/${businessDomainId}/strategy-map/info-capital-issues-data/${p.row.successFactorId}`
            : `/design/${businessDomainId}/strategy-map/info-capital-goals-data/${p.row.successFactorId}`;
          navigate(path);
        }}
        sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
      />
    </Box>
  );
}
